import { z } from 'zod';
import {
  currencyCodeSchema,
  finiteNumberSchema,
  nonNegativeNumberSchema,
  percentageValueSchema,
  vatRateSchema,
} from './money';

export const editIntentSourceSchema = z.enum([
  'frontend',
  'backend',
  'migration',
  'test',
]);

export const editIntentTargetSchema = z.object({
  itemId: z.string().min(1),
  lineId: z.string().min(1).nullable().optional(),
  roomId: z.string().min(1).nullable().optional(),
  travellerId: z.string().min(1).nullable().optional(),
  thresholdId: z.string().min(1).nullable().optional(),
  periodId: z.string().min(1).nullable().optional(),
});

const editIntentBaseSchema = z.object({
  intentId: z.string().min(1).optional(),
  sequence: z.number().int().nonnegative().optional(),
  source: editIntentSourceSchema.optional(),
  target: editIntentTargetSchema,
});

export const editIntentPriceComponentSchema = z.object({
  currency: currencyCodeSchema,
  cost: finiteNumberSchema.nullable(),
  netCost: finiteNumberSchema.nullable(),
  unitPrice: finiteNumberSchema.nullable(),
  // Base-currency provenance for `unitPrice` — see normalizedPriceComponentSchema.
  baseUnitPrice: finiteNumberSchema.nullable().optional(),
  finalPrice: finiteNumberSchema.nullable(),
  finalPriceWithTax: finiteNumberSchema.nullable(),
  profit: finiteNumberSchema.nullable(),
  markupPercent: percentageValueSchema.nullable(),
  commissionPercent: percentageValueSchema.nullable(),
  units: nonNegativeNumberSchema.nullable(),
  costVat: vatRateSchema.nullable(),
  priceVat: vatRateSchema.nullable(),
  isGross: z.boolean(),
  useNetCost: z.boolean(),
});

export const editIntentGroupThresholdSchema = z.object({
  id: z.string().min(1).optional(),
  travellerId: z.string().min(1).nullable().optional(),
  additionalTravellerId: z.string().min(1).nullable().optional(),
  minTravellers: z.number().int().nonnegative(),
  maxTravellers: z.number().int().nonnegative().nullable(),
  adultPrice: editIntentPriceComponentSchema.nullable(),
  childPrice: editIntentPriceComponentSchema.nullable(),
  additionalAdultPrice: editIntentPriceComponentSchema.nullable(),
  additionalChildPrice: editIntentPriceComponentSchema.nullable(),
});

export const editIntentNightPeriodSchema = z.object({
  id: z.string().min(1).optional(),
  roomId: z.string().min(1),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  startNight: z.number().int().nonnegative().nullable(),
  nightCount: z.number().int().nonnegative(),
  price: editIntentPriceComponentSchema,
});

const editIntentRoomPricingModeSchema = z.enum([
  'flat',
  'periods',
  'legacySinglePeriodExpansion',
]);

const editIntentRoomUnitSchema = z.enum(['UNIT', 'DAY', 'NIGHT']);

export const editIntentRoomSchema = z.object({
  id: z.string().min(1),
  sourceId: z.string().min(1).nullable().optional(),
  roomTypeId: z.string().min(1).nullable().optional(),
  label: z.string().nullable().optional(),
  unit: editIntentRoomUnitSchema,
  pricingMode: editIntentRoomPricingModeSchema,
  quantity: nonNegativeNumberSchema,
  nights: z.number().int().nonnegative().nullable(),
  price: editIntentPriceComponentSchema,
  pricePeriods: z.array(editIntentNightPeriodSchema),
});

export const editIntentNewRoomPeriodSchema = editIntentNightPeriodSchema.extend(
  {
    id: z.string().min(1),
  }
);

export const setCostIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setCost'),
  value: finiteNumberSchema,
});

export const setMarkupIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setMarkup'),
  valuePercent: percentageValueSchema,
});

export const setCommissionIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setCommission'),
  valuePercent: percentageValueSchema,
});

export const setUnitPriceIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setUnitPrice'),
  value: finiteNumberSchema,
});

export const setFinalPriceIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setFinalPrice'),
  value: finiteNumberSchema,
});

export const setProfitIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setProfit'),
  value: finiteNumberSchema,
});

export const setUnitsIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setUnits'),
  value: nonNegativeNumberSchema,
});

export const setCostVatIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setCostVat'),
  vat: vatRateSchema,
});

export const setPriceVatIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setPriceVat'),
  vat: vatRateSchema,
});

export const setCurrencyIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setCurrency'),
  currency: currencyCodeSchema,
});

export const setPriceTypeIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setPriceType'),
  isGross: z.boolean(),
});

export const setTravellerQuantityIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setTravellerQuantity'),
  travellerId: z.string().min(1),
  quantity: z.number().int().nonnegative(),
});

export const addTravellerLineIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('addTravellerLine'),
  travellerId: z.string().min(1),
  quantity: z.number().int().nonnegative(),
});

export const removeTravellerLineIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('removeTravellerLine'),
  travellerId: z.string().min(1),
});

export const setGroupThresholdsIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setGroupThresholds'),
  thresholds: z.array(editIntentGroupThresholdSchema),
});

export const setNightPeriodIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setNightPeriod'),
  periods: z.array(editIntentNightPeriodSchema),
});

export const setRoomTypeIdIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setRoomTypeId'),
  roomTypeId: z.string().min(1),
  roomTypeLabel: z.string().min(1).nullable().optional(),
});

export const setRoomUnitIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setRoomUnit'),
  unit: editIntentRoomUnitSchema,
  periodId: z.string().min(1).optional(),
});

const editIntentSingleStructureUnitSchema = z.enum([
  'SINGLE',
  'UNIT',
  'DAY',
  'HOUR',
  'NIGHT',
  'PIECE',
  'VEHICLE',
]);

export const setSingleStructureUnitIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setSingleStructureUnit'),
  unit: editIntentSingleStructureUnitSchema,
});

const editIntentItemPricingTypeSchema = z.enum([
  'SINGLE',
  'UNIT',
  'DAY',
  'HOUR',
  'NIGHT',
  'PIECE',
  'VEHICLE',
  'PAX',
  'GROUP',
]);

export const setItemPricingTypeIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('setItemPricingType'),
  pricingType: editIntentItemPricingTypeSchema,
});

// Full-replacement per-room traveller distribution (legacy
// TravellersPerRoomDistributionInput wrote `room.travellers` metadata).
export const setRoomTravellerDistributionIntentSchema =
  editIntentBaseSchema.extend({
    type: z.literal('setRoomTravellerDistribution'),
    travellers: z.array(
      z.object({
        travellerId: z.string().min(1),
        quantity: z.number().int().nonnegative(),
        label: z.string().nullable().optional(),
      })
    ),
  });

export const addRoomIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('addRoom'),
  room: editIntentRoomSchema,
});

export const deleteRoomIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('deleteRoom'),
});

export const addRoomPeriodIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('addRoomPeriod'),
  period: editIntentNewRoomPeriodSchema,
});

export const deleteRoomPeriodIntentSchema = editIntentBaseSchema.extend({
  type: z.literal('deleteRoomPeriod'),
});

export const pricingEditIntentSchema = z.discriminatedUnion('type', [
  setCostIntentSchema,
  setMarkupIntentSchema,
  setCommissionIntentSchema,
  setUnitPriceIntentSchema,
  setFinalPriceIntentSchema,
  setProfitIntentSchema,
  setUnitsIntentSchema,
  setCostVatIntentSchema,
  setPriceVatIntentSchema,
  setCurrencyIntentSchema,
  setPriceTypeIntentSchema,
  setTravellerQuantityIntentSchema,
  addTravellerLineIntentSchema,
  removeTravellerLineIntentSchema,
  setGroupThresholdsIntentSchema,
  setNightPeriodIntentSchema,
  setRoomTypeIdIntentSchema,
  setRoomUnitIntentSchema,
  setSingleStructureUnitIntentSchema,
  setItemPricingTypeIntentSchema,
  setRoomTravellerDistributionIntentSchema,
  addRoomIntentSchema,
  deleteRoomIntentSchema,
  addRoomPeriodIntentSchema,
  deleteRoomPeriodIntentSchema,
]);

export const orderedEditIntentsSchema = z.array(pricingEditIntentSchema);

export type EditIntentSource = z.infer<typeof editIntentSourceSchema>;
export type EditIntentTarget = z.infer<typeof editIntentTargetSchema>;
export type EditIntentPriceComponent = z.infer<
  typeof editIntentPriceComponentSchema
>;
export type EditIntentRoom = z.infer<typeof editIntentRoomSchema>;
export type EditIntentNewRoomPeriod = z.infer<
  typeof editIntentNewRoomPeriodSchema
>;
export type PricingEditIntent = z.infer<typeof pricingEditIntentSchema>;
export type OrderedEditIntents = z.infer<typeof orderedEditIntentsSchema>;
